import { useMemo } from 'react';
import { FileText, Layers, Lock, Palette, Smartphone } from 'lucide-react';
import { LoSiteBlockView } from '@/components/lyrraone/LoSiteBlockView';
import { LoSiteTemplateThumbnail, type LoSiteTemplatePreviewMeta } from '@/components/lyrraone/LoSiteTemplateThumbnail';
import { ensureLoSiteBlockIds } from '@/lib/lyrraone-site-blocks';
import { getLoSiteTheme, getLoSiteThemeRootClass } from '@/lib/lyrraone-site-themes';
import { getLoSiteThemeProfileStyle } from '@/lib/lyrraone-site-theme-profiles';
import { cn } from '@/lib/utils';
import '@/styles/lyrraone-landing.css';
import '@/styles/lo-site-template-live-preview.css';

export type LoSiteTemplateLivePreviewData = {
  id: string;
  name: string;
  description: string;
  category: string;
  tags: string[];
  pageCount: number;
  themeId: string;
  preview: LoSiteTemplatePreviewMeta;
  homePreview: Array<Record<string, unknown>>;
};

interface LoSiteTemplateLivePreviewProps {
  template: LoSiteTemplateLivePreviewData;
  siteSlug?: string;
  className?: string;
}

function slugifyName(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 24) || 'mon-site';
}

export function LoSiteTemplateLivePreview({
  template,
  siteSlug = 'preview',
  className,
}: LoSiteTemplateLivePreviewProps) {
  const blocks = useMemo(
    () => ensureLoSiteBlockIds(template.homePreview).slice(0, 4),
    [template.homePreview],
  );
  const themeMeta = getLoSiteTheme(template.themeId);
  const themeRootClass = getLoSiteThemeRootClass(template.themeId);
  const fakeUrl = `${slugifyName(template.name)}.lyrraone.site`;

  return (
    <div
      className={cn('lo-tpl-live-preview', className)}
      key={template.id}
      style={{
        '--lo-tpl-accent': template.preview.accent,
        '--lo-tpl-accent-soft': template.preview.accentSoft,
      } as React.CSSProperties}
    >
      <div className="lo-tpl-live-preview__ambient" aria-hidden />

      <div className="lo-tpl-live-preview__stage">
        <div className="lo-tpl-live-preview__browser">
          <div className="lo-tpl-live-preview__browser-bar">
            <div className="lo-tpl-live-preview__traffic" aria-hidden>
              <span className="lo-tpl-live-preview__traffic-dot lo-tpl-live-preview__traffic-dot--close" />
              <span className="lo-tpl-live-preview__traffic-dot lo-tpl-live-preview__traffic-dot--min" />
              <span className="lo-tpl-live-preview__traffic-dot lo-tpl-live-preview__traffic-dot--max" />
            </div>
            <div className="lo-tpl-live-preview__url">
              <Lock className="lo-tpl-live-preview__url-lock" aria-hidden />
              <span>{fakeUrl}</span>
            </div>
          </div>
          <div className="lo-tpl-live-preview__browser-body">
            <div
              className={cn('lo-tpl-live-preview__scaled lyrraone-landing', themeRootClass)}
              style={getLoSiteThemeProfileStyle(template.themeId)}
            >
              {blocks.map((block, i) => (
                <LoSiteBlockView
                  key={block.id ?? i}
                  block={block}
                  blockKey={block.id ?? String(i)}
                  formations={[]}
                  siteSlug={siteSlug}
                  siteTheme={template.themeId}
                />
              ))}
            </div>
            <div className="lo-tpl-live-preview__browser-fade" aria-hidden />
          </div>
        </div>

        <div className="lo-tpl-live-preview__phone" aria-hidden>
          <div className="lo-tpl-live-preview__phone-notch" />
          <div className="lo-tpl-live-preview__phone-screen">
            <LoSiteTemplateThumbnail
              homePreview={template.homePreview}
              preview={template.preview}
              name={template.name}
              siteTheme={template.themeId}
              compact
            />
          </div>
        </div>
      </div>

      <div className="lo-tpl-live-preview__info">
        <div className="lo-tpl-live-preview__badges">
          <span className="lo-tpl-live-preview__badge lo-tpl-live-preview__badge--category">
            {template.category}
          </span>
          <span className="lo-tpl-live-preview__badge">
            <Layers className="h-3 w-3" aria-hidden />
            {template.pageCount} page{template.pageCount > 1 ? 's' : ''}
          </span>
          <span className="lo-tpl-live-preview__badge">
            <Palette className="h-3 w-3" aria-hidden />
            {themeMeta.name}
          </span>
          <span className="lo-tpl-live-preview__badge lo-tpl-live-preview__badge--accent">
            <span className="lo-tpl-live-preview__swatch" style={{ background: template.preview.accent }} />
            Thème
          </span>
        </div>
        <h3 className="lo-tpl-live-preview__title">{template.name}</h3>
        <p className="lo-tpl-live-preview__desc">{template.description}</p>
        {template.tags.length > 0 ? (
          <div className="lo-tpl-live-preview__tags">
            <FileText className="h-3.5 w-3.5 shrink-0 opacity-50" aria-hidden />
            {template.tags.slice(0, 4).map((tag) => (
              <span key={tag} className="lo-tpl-live-preview__tag">#{tag}</span>
            ))}
          </div>
        ) : null}
        <p className="lo-tpl-live-preview__hint">
          <Smartphone className="h-3.5 w-3.5" aria-hidden />
          Aperçu desktop + mobile — personnalisable après application
        </p>
      </div>
    </div>
  );
}
